import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import { useNotifications } from '../../context/NotificationContext';
import { useAuth } from '../../context/AuthContext';
import { api, apiClient } from '../../api/client';
import { getState as getPushState, requestAndRegister, type PushState } from '../../services/webPush';
import './NotificationBell.css';

interface NotificationItem {
  _id: string;
  type:
    | 'direct_message' | 'event_comment' | 'join_request' | 'join_request_result'
    | 'event_invitation' | 'announcement' | 'event_reminder' | 'waitlist_promotion'
    | 'waitlist_added' | 'event_cancelled' | 'event_rescheduled' | 'event_capacity_full'
    | 'attendance_declined' | 'attendance_confirmed' | 'member_joined_group'
    | 'payment_recorded' | 'payment_due' | 'membership_expiring';
  reference: {
    event?: { _id: string; name: string; startTime: string };
    sender?: { _id: string; name: string };
    request?: number;
    type?: string;
    target?: number;
    member?: { _id: string; name: string };
    announcement?: { _id: string; message: string };
    status?: string;
    needsChoice?: boolean;
    // Snapshots + new-type payload fields.
    eventName?: string;
    memberName?: string;
    groupName?: string;
    membershipName?: string;
    targetName?: string;
    note?: string | null;
    amount?: string;
    what?: string | null;
    startTime?: string;
    previousStartTime?: string;
    periodEnd?: string;
    /** attendance_declined: charge still open for the declined event. */
    openCharge?: { intentId: number; balance: number } | null;
  };
  read: boolean;
  createdAt: string;
}

/** Per-device dismissal of the enable-notifications nudge. */
const PUSH_NUDGE_DISMISSED_KEY = 'eventik.pushNudgeDismissed';

/**
 * The header renders two NotificationBell instances (desktop and mobile
 * variants, one hidden by CSS at any width), each with its own state. Without
 * this event, dismissing the nudge in the visible one leaves the other still
 * showing its dot until remount — visible if the window is then resized across
 * the breakpoint.
 */
const PUSH_NUDGE_DISMISSED_EVENT = 'eventik:pushNudgeDismissed';

const NotificationBell: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { counts, markAllRead } = useNotifications();
  const { isAdmin, isOwner, isSuperAdmin, hasPermission } = useAuth();
  const [isOpen, setIsOpen] = useState(false);

  /**
   * Browser-notification nudge.
   *
   * Without a prompt here the channel dies unused: nothing asks on arrival (by
   * design — a `denied` result is sticky and unrecoverable from script), so the
   * only way in is a button buried in Preferences.
   *
   * Shown ONLY when the state is `default` — configured, supported, never
   * asked. Deliberately NOT for `denied`: script cannot reopen that decision,
   * so an indicator the user is powerless to clear would be pure nag.
   *
   * Dismissal is per-device in localStorage, not a server preference, because
   * notification permission is itself per-browser. A server flag would hide the
   * nudge on the user's other devices, where they never granted anything.
   */
  const [pushState, setPushState] = useState<PushState>(() => getPushState());
  const [enablingPush, setEnablingPush] = useState(false);
  const [nudgeDismissed, setNudgeDismissed] = useState<boolean>(() => {
    try {
      return localStorage.getItem(PUSH_NUDGE_DISMISSED_KEY) === '1';
    } catch {
      return false;
    }
  });

  const shouldNudge = pushState === 'default' && !nudgeDismissed;

  const dismissNudge = () => {
    setNudgeDismissed(true);
    try {
      localStorage.setItem(PUSH_NUDGE_DISMISSED_KEY, '1');
    } catch {
      // Private mode — the nudge simply returns next session.
    }
    window.dispatchEvent(new Event(PUSH_NUDGE_DISMISSED_EVENT));
  };

  // Keep the sibling bell instance in step (see the event's doc comment).
  useEffect(() => {
    const onDismissed = () => setNudgeDismissed(true);
    window.addEventListener(PUSH_NUDGE_DISMISSED_EVENT, onDismissed);
    return () => window.removeEventListener(PUSH_NUDGE_DISMISSED_EVENT, onDismissed);
  }, []);

  const [pushError, setPushError] = useState<string | null>(null);

  const handleEnablePush = async () => {
    setEnablingPush(true);
    setPushError(null);
    try {
      const result = await requestAndRegister();
      setPushState(result.state);
      // The permission decision is what the nudge is about, so dismiss as soon
      // as one exists — even if the token registration then failed. Reporting
      // that failure is a separate concern (below); conflating the two is what
      // left the nudge on screen until a reload.
      if (result.state !== 'default') dismissNudge();
      if (result.state === 'granted' && !result.registered) {
        setPushError(t('notificationPrefs.browserFailed'));
      }
    } finally {
      setEnablingPush(false);
    }
  };
  const [notifications, setNotifications] = useState<NotificationItem[]>([]);
  const [loading, setLoading] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const fetchNotifications = useCallback(async () => {
    try {
      setLoading(true);

      const { data } = await api.notifications.list();

      setNotifications(data as NotificationItem[]);
    } catch (err) {
      console.error('Failed to fetch notifications:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isOpen) {
      fetchNotifications();
    }
  }, [isOpen, fetchNotifications]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleMarkAllRead = async () => {
    // Mark all notifications as read (not just comments)
    await markAllRead();
    setNotifications(prev => prev.map(n => ({ ...n, read: true })));
  };

  const markNotificationRead = async (notificationId: string) => {
    try {
      await apiClient.put(`/api/notifications/${notificationId}/read`);
      setNotifications(prev =>
        prev.map(n => n._id === notificationId ? { ...n, read: true } : n)
      );
    } catch (err) {
      console.error('Failed to mark notification as read:', err);
    }
  };

  const formatTime = (timestamp: string): string => {
    const date = new Date(timestamp);
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 1) return t('notifications.justNow');
    if (minutes < 60) return t('notifications.minutesAgo', { count: minutes });
    if (hours < 24) return t('notifications.hoursAgo', { count: hours });
    if (days < 7) return t('notifications.daysAgo', { count: days });
    return date.toLocaleDateString();
  };

  // Show comment, approval, invitation, announcement, and reminder notifications (DMs are handled by chat bubble)
  // Everything except DMs, which the chat bubble owns.
  const displayNotifications = notifications.filter(n => n.type !== 'direct_message');

  // Derived from the total rather than a hardcoded list of buckets: the bell
  // shows every non-DM type, so a badge that sums five named buckets silently
  // ignores every bucket added later (attendance, eventUpdates, waitlist,
  // members, payments) and stays at 0 for those notifications.
  const badgeCount = Math.max(0, counts.total - counts.dm);

  const handleSeeAll = () => {
    setIsOpen(false);
    navigate(
      location.pathname.startsWith('/admin') ? '/admin/communication' : '/dashboard/communication'
    );
  };

  const handleNotificationClick = async (notification: NotificationItem) => {
    setIsOpen(false);

    // Mark as read when clicked
    if (!notification.read) {
      await markNotificationRead(notification._id);
    }

    const eventId = notification.reference.event?._id;

    if (notification.type === 'join_request') {
      navigate(`/admin/approvals?tab=${notification.reference.type || 'group'}`);
    } else if (notification.type === 'join_request_result') {
      navigate(notification.reference.needsChoice ? '/dashboard/memberships' : '/dashboard/activities');
    } else if (notification.type === 'event_comment') {
      if (eventId) {
        if (isAdmin || isOwner || isSuperAdmin) {
          navigate(`/admin/calendar?eventId=${eventId}`);
        } else {
          navigate(`/dashboard/calendar?eventId=${eventId}`);
        }
      }
    } else if (notification.type === 'event_invitation') {
      navigate(eventId ? `/dashboard/invitations?eventId=${eventId}` : '/dashboard/invitations');
    } else if (notification.type === 'announcement') {
      navigate('/dashboard/announcements');
    } else if (notification.type === 'event_reminder') {
      navigate(eventId ? `/dashboard/calendar?eventId=${eventId}` : '/dashboard/calendar');
    } else if (notification.type === 'waitlist_promotion' || notification.type === 'waitlist_added') {
      navigate(eventId ? `/dashboard/calendar?eventId=${eventId}` : '/dashboard/calendar');
    } else if (notification.type === 'event_cancelled') {
      // The event row is gone — never deep-link to it.
      navigate('/dashboard/calendar');
    } else if (notification.type === 'event_rescheduled') {
      navigate(eventId ? `/dashboard/calendar?eventId=${eventId}` : '/dashboard/calendar');
    } else if (
      notification.type === 'attendance_declined'
      && notification.reference.openCharge
      && hasPermission('payments:read')
    ) {
      // Money on the table: land on the Payment Overview, "not attending"
      // filter, this member — where "Cancel charge" lives. Only for a viewer
      // who can actually open that page; the audience is every scoped admin,
      // and a coach-style role without payments bits would hit "no permission".
      const memberId = notification.reference.member?._id;
      navigate(`/admin/payments/overview?declined=1${memberId ? `&memberId=${memberId}` : ''}`);
    } else if (
      notification.type === 'attendance_declined' ||
      notification.type === 'attendance_confirmed' ||
      notification.type === 'event_capacity_full'
    ) {
      // /admin/events/:id/attendance is not a declared route; the event detail
      // (with its attendance panel) opens from the calendar.
      navigate(eventId ? `/admin/calendar?eventId=${eventId}` : '/admin/calendar');
    } else if (notification.type === 'member_joined_group') {
      navigate('/admin/members');
    } else if (
      notification.type === 'payment_recorded' ||
      notification.type === 'payment_due' ||
      notification.type === 'membership_expiring'
    ) {
      navigate('/dashboard/payments');
    }
  };

  const getNotificationText = (notification: NotificationItem) => {
    if (notification.type === 'join_request') {
      return (
        <>
          {t('notifications.joinRequest')}
        </>
      );
    }
    if (notification.type === 'join_request_result') {
      return (
        <>
          {notification.reference.status === 'approved'
            ? t('notifications.requestApproved')
            : t('notifications.requestDenied')}
        </>
      );
    }
    if (notification.type === 'event_invitation') {
      return (
        <>
          {t('notifications.invitedTo')}{' '}
          <strong>{notification.reference.event?.name}</strong>
        </>
      );
    }
    if (notification.type === 'event_reminder') {
      const eventName = notification.reference.event?.name || '';
      const startTime = notification.reference.event?.startTime
        ? new Date(notification.reference.event.startTime).toLocaleString(undefined, {
            weekday: 'short', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
          })
        : '';
      return (
        <>
          {t('notifications.eventReminder', { eventName, time: startTime })}
        </>
      );
    }
    if (notification.type === 'announcement') {
      const preview = notification.reference.announcement?.message || '';
      const truncated = preview.length > 60 ? preview.substring(0, 60) + '...' : preview;
      return (
        <>
          <strong>{notification.reference.sender?.name}</strong>
          {' '}{t('notifications.sentAnnouncement')}
          {truncated && <> &mdash; {truncated}</>}
        </>
      );
    }
    if (notification.type === 'waitlist_promotion') {
      return (
        <>
          {t('notifications.promotedFromWaitlist')}{' '}
          <strong>{notification.reference.event?.name}</strong>
        </>
      );
    }

    // ---- types added by the notifications rework -------------------------
    const ref = notification.reference;
    // Prefer the live hydrated name, fall back to the stored snapshot (the
    // event row is gone for a cancellation).
    const eventName = ref.event?.name || ref.eventName || '';
    const memberName = ref.member?.name || ref.memberName || '';

    if (notification.type === 'waitlist_added') {
      return <>{t('notifications.waitlistAdded', { member: memberName, event: eventName })}</>;
    }
    if (notification.type === 'event_cancelled') {
      return <>{t('notifications.eventCancelled', { event: eventName })}</>;
    }
    if (notification.type === 'event_rescheduled') {
      return <>{t('notifications.eventRescheduled', { event: eventName })}</>;
    }
    if (notification.type === 'event_capacity_full') {
      return <>{t('notifications.eventCapacityFull', { event: eventName })}</>;
    }
    if (notification.type === 'attendance_declined') {
      const openCharge = ref.openCharge;
      return (
        <>
          {openCharge
            ? t('notifications.attendanceDeclinedCharge', {
                member: memberName,
                event: eventName,
                amount: `${new Intl.NumberFormat('cs-CZ', { maximumFractionDigits: 2 }).format(openCharge.balance)} Kč`,
              })
            : t('notifications.attendanceDeclined', { member: memberName, event: eventName })}
          {ref.note && <> &mdash; <em>{ref.note}</em></>}
        </>
      );
    }
    if (notification.type === 'attendance_confirmed') {
      return <>{t('notifications.attendanceConfirmed', { member: memberName, event: eventName })}</>;
    }
    if (notification.type === 'member_joined_group') {
      return <>{t('notifications.memberJoinedGroup', { member: memberName, group: ref.groupName || '' })}</>;
    }
    if (notification.type === 'payment_recorded') {
      return <>{t('notifications.paymentRecorded', { amount: ref.amount || '', member: memberName })}</>;
    }
    if (notification.type === 'payment_due') {
      return <>{t('notifications.paymentDue', { amount: ref.amount || '', member: memberName })}</>;
    }
    if (notification.type === 'membership_expiring') {
      return (
        <>
          {t('notifications.membershipExpiring', {
            member: memberName,
            membership: ref.membershipName || '',
          })}
        </>
      );
    }

    return (
      <>
        <strong>{notification.reference.sender?.name}</strong>
        {' '}{t('notifications.commentedOn')}{' '}
        <strong>{notification.reference.event?.name}</strong>
      </>
    );
  };

  const getNotificationIcon = (notification: NotificationItem) => {
    if (notification.type === 'join_request') {
      return (
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" width="16" height="16">
          <path fillRule="evenodd" d="M8.25 6.75a3.75 3.75 0 117.5 0 3.75 3.75 0 01-7.5 0zM15.75 9.75a3 3 0 116 0 3 3 0 01-6 0zM2.25 9.75a3 3 0 116 0 3 3 0 01-6 0zM6.31 15.117A6.745 6.745 0 0112 12a6.745 6.745 0 016.709 7.498.75.75 0 01-.372.568A12.696 12.696 0 0112 21.75c-2.305 0-4.47-.612-6.337-1.684a.75.75 0 01-.372-.568 6.787 6.787 0 011.019-4.38z" clipRule="evenodd" />
          <path d="M5.082 14.254a8.287 8.287 0 00-1.308 5.135 9.687 9.687 0 01-1.764-.44l-.115-.04a.563.563 0 01-.373-.487l-.01-.121a3.75 3.75 0 013.57-4.047zM20.226 19.389a8.287 8.287 0 00-1.308-5.135 3.75 3.75 0 013.57 4.047l-.01.121a.563.563 0 01-.373.486l-.115.04c-.567.2-1.156.349-1.764.441z" />
        </svg>
      );
    }
    if (notification.type === 'join_request_result') {
      return (
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" width="16" height="16">
          <path fillRule="evenodd" d="M2.25 12c0-5.385 4.365-9.75 9.75-9.75s9.75 4.365 9.75 9.75-4.365 9.75-9.75 9.75S2.25 17.385 2.25 12zm13.36-1.814a.75.75 0 10-1.22-.872l-3.236 4.53L9.53 12.22a.75.75 0 00-1.06 1.06l2.25 2.25a.75.75 0 001.14-.094l3.75-5.25z" clipRule="evenodd" />
        </svg>
      );
    }
    if (notification.type === 'event_invitation') {
      return (
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" width="16" height="16">
          <path d="M12.75 12.75a.75.75 0 11-1.5 0 .75.75 0 011.5 0zM7.5 15.75a.75.75 0 100-1.5.75.75 0 000 1.5zM8.25 17.25a.75.75 0 11-1.5 0 .75.75 0 011.5 0zM9.75 15.75a.75.75 0 100-1.5.75.75 0 000 1.5zM10.5 17.25a.75.75 0 11-1.5 0 .75.75 0 011.5 0zM12 15.75a.75.75 0 100-1.5.75.75 0 000 1.5zM12.75 17.25a.75.75 0 11-1.5 0 .75.75 0 011.5 0zM14.25 15.75a.75.75 0 100-1.5.75.75 0 000 1.5zM15 17.25a.75.75 0 11-1.5 0 .75.75 0 011.5 0zM16.5 15.75a.75.75 0 100-1.5.75.75 0 000 1.5zM15 12.75a.75.75 0 11-1.5 0 .75.75 0 011.5 0zM16.5 13.5a.75.75 0 100-1.5.75.75 0 000 1.5z" />
          <path fillRule="evenodd" d="M6.75 2.25A.75.75 0 017.5 3v1.5h9V3A.75.75 0 0118 3v1.5h.75a3 3 0 013 3v11.25a3 3 0 01-3 3H5.25a3 3 0 01-3-3V7.5a3 3 0 013-3H6V3a.75.75 0 01.75-.75zm13.5 9a1.5 1.5 0 00-1.5-1.5H5.25a1.5 1.5 0 00-1.5 1.5v7.5a1.5 1.5 0 001.5 1.5h13.5a1.5 1.5 0 001.5-1.5v-7.5z" clipRule="evenodd" />
        </svg>
      );
    }
    if (notification.type === 'event_reminder') {
      return (
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" width="16" height="16">
          <path fillRule="evenodd" d="M12 2.25c-5.385 0-9.75 4.365-9.75 9.75s4.365 9.75 9.75 9.75 9.75-4.365 9.75-9.75S17.385 2.25 12 2.25zM12.75 6a.75.75 0 00-1.5 0v6c0 .414.336.75.75.75h4.5a.75.75 0 000-1.5h-3.75V6z" clipRule="evenodd" />
        </svg>
      );
    }
    if (notification.type === 'announcement') {
      return (
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" width="16" height="16">
          <path d="M16.881 4.346A23.112 23.112 0 018.25 6H7.5a5.25 5.25 0 00-.88 10.427 21.593 21.593 0 001.378 3.94c.464 1.004 1.674 1.32 2.582.796l.657-.379c.88-.508 1.165-1.592.772-2.468a17.116 17.116 0 01-.628-1.607c1.918.258 3.76.75 5.5 1.446A21.727 21.727 0 0018 11.25c0-2.413-.393-4.735-1.119-6.904zM18.26 3.74a23.22 23.22 0 011.24 7.51 23.22 23.22 0 01-1.24 7.51c-.055.161.044.343.206.398a.3.3 0 00.095.017c.128 0 .247-.08.29-.207a23.79 23.79 0 001.149-4.395 23.79 23.79 0 00-1.149-4.395.3.3 0 00-.391-.205.299.299 0 00-.2.367z" />
        </svg>
      );
    }
    return (
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" width="16" height="16">
        <path fillRule="evenodd" d="M4.848 2.771A49.144 49.144 0 0112 2.25c2.43 0 4.817.178 7.152.52 1.978.292 3.348 2.024 3.348 3.97v6.02c0 1.946-1.37 3.678-3.348 3.97a48.901 48.901 0 01-3.476.383.39.39 0 00-.297.17l-2.755 4.133a.75.75 0 01-1.248 0l-2.755-4.133a.39.39 0 00-.297-.17 48.9 48.9 0 01-3.476-.384c-1.978-.29-3.348-2.024-3.348-3.97V6.741c0-1.946 1.37-3.68 3.348-3.97z" clipRule="evenodd" />
      </svg>
    );
  };

  return (
    <div className="notification-bell" ref={dropdownRef}>
      <button
        className="notification-bell__button"
        onClick={() => setIsOpen(!isOpen)}
        aria-label="Notifications"
      >
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" width="22" height="22">
          <path fillRule="evenodd" d="M5.25 9a6.75 6.75 0 0113.5 0v.75c0 2.123.8 4.057 2.118 5.52a.75.75 0 01-.297 1.206c-1.544.57-3.16.99-4.831 1.243a3.75 3.75 0 11-7.48 0 24.585 24.585 0 01-4.831-1.244.75.75 0 01-.298-1.205A8.217 8.217 0 005.25 9.75V9zm4.502 8.9a2.25 2.25 0 104.496 0 25.057 25.057 0 01-4.496 0z" clipRule="evenodd" />
        </svg>
        {badgeCount > 0 && (
          <span className="notification-bell__badge">
            {badgeCount > 99 ? '99+' : badgeCount}
          </span>
        )}
        {/* Sits opposite the unread badge so the two never overlap. */}
        {shouldNudge && (
          <span
            className="notification-bell__nudge-dot"
            title={t('notificationPrefs.browserPrompt')}
            aria-hidden="true"
          >
            !
          </span>
        )}
      </button>

      {isOpen && (
        <div className="notification-bell__dropdown">
          <div className="notification-bell__header">
            <span>{t('notifications.title')}</span>
            {badgeCount > 0 && (
              <button
                className="notification-bell__mark-read"
                onClick={handleMarkAllRead}
              >
                {t('notifications.markAllRead')}
              </button>
            )}
          </div>

          {pushError && (
            <div className="notification-bell__nudge notification-bell__nudge--error">
              <p className="notification-bell__nudge-text">{pushError}</p>
            </div>
          )}

          {shouldNudge && (
            <div className="notification-bell__nudge">
              <p className="notification-bell__nudge-text">
                {t('notifications.enablePushPrompt')}
              </p>
              <div className="notification-bell__nudge-actions">
                <button
                  type="button"
                  className="notification-bell__nudge-enable"
                  onClick={handleEnablePush}
                  disabled={enablingPush}
                >
                  {enablingPush
                    ? t('notificationPrefs.browserEnabling')
                    : t('notificationPrefs.browserEnable')}
                </button>
                <button
                  type="button"
                  className="notification-bell__nudge-dismiss"
                  onClick={dismissNudge}
                >
                  {t('notifications.enablePushDismiss')}
                </button>
              </div>
            </div>
          )}

          <div className="notification-bell__list">
            {loading ? (
              <div className="notification-bell__loading">
                <div className="notification-bell__spinner"></div>
              </div>
            ) : displayNotifications.length === 0 ? (
              <div className="notification-bell__empty">
                {t('notifications.noNotifications')}
              </div>
            ) : (
              displayNotifications.map(notification => (
                <div
                  key={notification._id}
                  className={`notification-bell__item ${!notification.read ? 'notification-bell__item--unread' : ''}`}
                  onClick={() => handleNotificationClick(notification)}
                  style={{ cursor: 'pointer' }}
                >
                  <div className="notification-bell__icon">
                    {getNotificationIcon(notification)}
                  </div>
                  <div className="notification-bell__content">
                    <p className="notification-bell__text">
                      {getNotificationText(notification)}
                    </p>
                    <span className="notification-bell__time">
                      {formatTime(notification.createdAt)}
                    </span>
                  </div>
                </div>
              ))
            )}
          </div>

          <div className="notification-bell__footer">
            <button
              type="button"
              className="notification-bell__see-all"
              onClick={handleSeeAll}
            >
              {t('notifications.seeAll')} &rarr;
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
